/**
 * Renders a `TranslatableText` and never lies about it.
 *
 * When the server machine-translated the field, the rendered text is tagged
 * with a "Translated" badge naming the source language, and the page-level
 * "show original" switch flips every tagged field back at once. Silently
 * showing machine output as if a human wrote it is the one thing the
 * localisation model in the API contract exists to prevent.
 *
 * It also decides *how* to render. `TranslatableText.format` says whether the
 * value is prose or Ivanti's rich text; markup goes to `<RichText>`, which
 * builds React elements from it rather than handing a string to the DOM, and
 * plain text keeps the escaped `whitespace-pre-wrap` span it always had.
 * `multiline` is a plain-text concern only: `whitespace-pre-wrap` on markup
 * would honour every newline between the tags and wreck the block layout.
 */

import { Badge, Typography } from "@norican/react";

import { RichText } from "@/components/common";
import { useLanguage, useT } from "@/i18n";

import type { TranslatableText } from "../api/contract";
import { displayLang, displayText, isHtmlFormat, isMachineTranslated } from "../lib/translatable";
import { useTranslationPreference } from "./translation-preference";

export interface TranslatableTextViewProps {
  value: TranslatableText | null | undefined;
  /** Rendered when the field is absent or empty. */
  fallback?: string;
  /**
   * Preserve newlines from a multi-line journal entry or description.
   *
   * Ignored for an HTML value, which carries its own block structure.
   */
  multiline?: boolean;
  /** Hide the "Translated" badge — for dense places such as a grid cell. */
  hideBadge?: boolean;
  className?: string;
}

/** Names a BCP-47 tag in the reader's own language ("Deutsch" → "German"). */
function languageName(tag: string | null | undefined, locale: string): string {
  if (tag === null || tag === undefined || tag.length === 0) {
    return "";
  }
  try {
    return new Intl.DisplayNames([locale], { type: "language" }).of(tag) ?? tag;
  } catch {
    // An unexpected tag from the provider must not take the page down.
    return tag;
  }
}

export function TranslatableTextView({
  value,
  fallback,
  multiline = false,
  hideBadge = false,
  className,
}: TranslatableTextViewProps) {
  const { t } = useT("tickets");
  const { locale } = useLanguage();
  const { showOriginal } = useTranslationPreference();

  const translated = isMachineTranslated(value);
  const html = isHtmlFormat(value);
  const text = displayText(value, showOriginal);
  const lang = displayLang(value, showOriginal);

  if (text.length === 0) {
    return <span className={className}>{fallback ?? "—"}</span>;
  }

  const body = html ? (
    <RichText html={text} lang={lang} />
  ) : (
    <span className={multiline ? "whitespace-pre-wrap" : undefined} lang={lang}>
      {text}
    </span>
  );

  // Markup brings block elements with it, so the wrapper has to be one too —
  // a `<div>` inside a `<span>` is invalid and collapses the layout.
  const Wrapper = html ? "div" : "span";

  if (!translated || hideBadge) {
    return <Wrapper className={className}>{body}</Wrapper>;
  }

  return (
    <Wrapper className={className}>
      {body}{" "}
      <Badge
        tone="info"
        variant="subtle"
        size="xs"
        title={
          showOriginal
            ? t("translated.originalNote", { language: languageName(value?.original_lang, locale) })
            : t("translated.note", { language: languageName(value?.original_lang, locale) })
        }
      >
        {showOriginal ? t("translated.originalBadge") : t("translated.badge")}
      </Badge>
    </Wrapper>
  );
}

export interface TranslationNoticeProps {
  /** True when at least one field on this page came back machine-translated. */
  translated: boolean;
  /** True when the server could not translate everything it was asked to. */
  partial?: boolean;
}

/**
 * The page-level explanation that sits beside the "show original" switch.
 *
 * Split out from the switch itself so the detail header can place the control
 * in its action cluster and the note under the title.
 */
export function TranslationNotice({ translated, partial = false }: TranslationNoticeProps) {
  const { t } = useT("tickets");

  if (!translated && !partial) {
    return null;
  }

  return (
    <Typography variant="caption" tone="muted">
      {partial ? t("translated.partial") : t("translated.pageNote")}
    </Typography>
  );
}
